import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Megaphone, Share2, TrendingUp, MapPin, Video, Trophy,
  Users, Target, Sparkles, Eye, Clapperboard, BadgeDollarSign, Lock,
} from 'lucide-react';
import { getSponsor } from '../../api';
import { ReactComponent as LogoIcon } from '../../assets/images/logo.svg';

const ORANGE = '#e57605';
const ORANGE_LIGHT = '#f29b2a';

const fmt = (n: number) => Math.round(n).toLocaleString('pt-BR');

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] as any },
  }),
};

function StatCard({ value, label, icon: Icon, i }: any) {
  return (
    <motion.div
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="relative rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: ORANGE }} />
      <Icon className="w-6 h-6 mb-3" style={{ color: ORANGE_LIGHT }} />
      <div className="text-4xl md:text-5xl font-extrabold text-white tracking-tight">{value}</div>
      <div className="text-sm text-zinc-400 mt-1">{label}</div>
    </motion.div>
  );
}

function Pillar({ icon: Icon, title, text, i }: any) {
  return (
    <motion.div
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 hover:border-zinc-700 transition-colors"
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(229,118,5,0.12)', border: '1px solid rgba(229,118,5,0.3)' }}>
        <Icon className="w-5 h-5" style={{ color: ORANGE_LIGHT }} />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
    </motion.div>
  );
}

// Mini gráfico de visualizações subindo (SVG animado)
function RisingChart() {
  const line = 'M4 66 L20 58 L36 60 L52 46 L68 48 L84 32 L100 34 L116 12';
  return (
    <div className="shrink-0 w-[110px] h-[78px] rounded-xl bg-black/40 border border-zinc-800 px-2 pt-2 pb-4 relative overflow-hidden">
      <svg viewBox="0 0 120 80" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="rcFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ORANGE} stopOpacity="0.4" />
            <stop offset="100%" stopColor={ORANGE} stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path d={`${line} L116 76 L4 76 Z`} fill="url(#rcFill)" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.7, duration: 0.8 }} />
        <motion.path d={line} fill="none" stroke={ORANGE_LIGHT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: 'easeInOut' }} />
        <motion.circle cx="116" cy="12" r="3.5" fill={ORANGE_LIGHT} initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.15, type: 'spring', stiffness: 300 }} />
      </svg>
      <span className="absolute bottom-1.5 left-2 text-[9px] uppercase tracking-wider text-zinc-500">Visualizações</span>
    </div>
  );
}

// Tela de senha (4 dígitos = últimos 4 do telefone da arena)
function PasswordGate({ arenaName, onSubmit, error }: any) {
  const [value, setValue] = useState('');
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] blur-[140px] opacity-20 pointer-events-none" style={{ background: ORANGE }} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-sm rounded-3xl border border-zinc-800 bg-zinc-900/60 backdrop-blur-xl p-8 text-center"
      >
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(229,118,5,0.12)', border: '1px solid rgba(229,118,5,0.3)' }}>
          <Lock className="w-6 h-6" style={{ color: ORANGE_LIGHT }} />
        </div>
        <h1 className="text-xl font-bold text-white">{arenaName}</h1>
        <p className="text-sm text-zinc-400 mt-2 mb-6">Página exclusiva. Digite a senha de 4 dígitos para acessar.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); if (value.length === 4) onSubmit(value); }}
        >
          <input
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric"
            autoFocus
            placeholder="••••"
            className="w-full text-center tracking-[0.6em] text-2xl font-bold py-3 rounded-xl bg-black/40 border text-white outline-none focus:ring-2"
            style={{ borderColor: error ? '#ef4444' : '#3f3f46', caretColor: ORANGE_LIGHT }}
          />
          {error && <p className="text-red-400 text-xs mt-2">Senha incorreta. Tente novamente.</p>}
          <button
            type="submit"
            disabled={value.length !== 4}
            className="w-full mt-5 py-3 rounded-xl font-semibold text-black transition-opacity disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${ORANGE_LIGHT}, ${ORANGE})` }}
          >
            Entrar
          </button>
        </form>
        <p className="text-[11px] text-zinc-600 mt-5">A senha são os últimos 4 dígitos do telefone da arena.</p>
      </motion.div>
    </div>
  );
}

function Sponsor() {
  const { arenaSlug } = useParams();
  const [ratio, setRatio] = useState('9 / 16');
  const [videoReady, setVideoReady] = useState(false);
  const [pass, setPass] = useState(() => (arenaSlug ? sessionStorage.getItem(`sp_${arenaSlug}`) || '' : ''));

  const { data, isLoading, isError } = useQuery({
    queryKey: ['getSponsor', arenaSlug, pass],
    queryFn: getSponsor,
    enabled: !!arenaSlug,
    refetchOnWindowFocus: false,
  });

  // Persiste a senha só quando ela funciona (página desbloqueada)
  useEffect(() => {
    if (arenaSlug && pass && data && !data.locked && !data.error) {
      sessionStorage.setItem(`sp_${arenaSlug}`, pass);
    }
  }, [arenaSlug, pass, data]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-zinc-700 animate-spin" style={{ borderTopColor: ORANGE }} />
          <p className="text-zinc-500 text-sm">Carregando…</p>
        </div>
      </div>
    );
  }

  if (isError || !data || data.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0a0a0a] px-6 text-center">
        <Clapperboard className="w-12 h-12 text-zinc-600" />
        <h1 className="text-2xl font-bold text-white">Arena não encontrada</h1>
        <p className="text-zinc-500 max-w-md">Não localizamos uma arena com esse endereço. Confira o link com a sua arena Apertai.</p>
        <Link to="/" className="mt-2 text-sm font-medium" style={{ color: ORANGE_LIGHT }}>Voltar ao início</Link>
      </div>
    );
  }

  // Bloqueada por senha
  if (data.locked) {
    return (
      <PasswordGate
        arenaName={data.arenaName}
        error={!!pass}
        onSubmit={(pin: string) => setPass(pin)}
      />
    );
  }

  const {
    arenaName, city, courtsCount, monthTotal = 0, lastMonthEstimate = 0, perCourt = [], sample,
  } = data;

  // "mês passado" usa a ESTIMATIVA (média recente × 30, p/ baixo) porque o
  // bucket guarda só ~10-14 dias e o contado real fica artificialmente baixo.
  const lastMonth = lastMonthEstimate;

  // Regras de destaque (inflar sem mentir):
  // 1) Se os 2 meses juntos não passam de 100, soma os dois e mostra o atual à parte.
  // 2) Senão, se o mês atual está fraco (<20), destaca o mês passado.
  // 3) Senão, destaca o mês atual.
  const combined = monthTotal + lastMonth;
  const small = combined > 0 && combined < 100;
  const nearHundred = small && combined > 80;            // 80–100 -> "Quase 100"
  const useLast = !small && monthTotal < 20 && lastMonth > monthTotal;

  const sThis = `+${fmt(monthTotal)}`;
  const sLast = `+de ${fmt(lastMonth)}`;                 // estimativa -> "mais de"

  const heroValue = small ? (nearHundred ? 'Quase 100' : `+${fmt(combined)}`) : useLast ? sLast : sThis;
  const heroPeriod = small ? 'recentes' : useLast ? 'no mês passado' : 'este mês';
  const otherValue = small || useLast ? sThis : sLast;
  const otherNumeric = small || useLast ? monthTotal : lastMonth;
  const otherPeriod = small || useLast ? 'este mês' : 'no mês passado';
  const otherPrefix = small || useLast ? 'já ' : '';
  const viewsBase = small ? combined : useLast ? lastMonth : monthTotal;
  const hasData = combined > 0;
  const estViews = viewsBase * 500; // conservador: 500 views por vídeo

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Top bar */}
      <header className="relative z-20 flex items-center justify-between max-w-7xl mx-auto px-5 md:px-8 py-5">
        <Link to="/" className="flex items-center gap-2" style={{ color: '#fff' }}>
          <LogoIcon style={{ height: 30, width: 'auto' }} />
        </Link>
        <span className="text-xs uppercase tracking-widest text-zinc-500">Patrocínio</span>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pt-6 md:pt-12 pb-16">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] blur-[120px] opacity-25 pointer-events-none" style={{ background: `radial-gradient(circle, ${ORANGE}, transparent 70%)` }} />
        <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left */}
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-5" style={{ background: 'rgba(229,118,5,0.12)', border: '1px solid rgba(229,118,5,0.3)', color: ORANGE_LIGHT }}>
              <MapPin className="w-3.5 h-3.5" />
              {arenaName} · {city}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Coloque sua marca
              <br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(120deg, ${ORANGE_LIGHT}, #ffce8a)` }}>
                dentro de cada jogo.
              </span>
            </h1>
            <p className="text-lg text-zinc-400 mt-6 max-w-xl leading-relaxed">
              Toda partida na <span className="text-white font-medium">{arenaName}</span> vira vídeo — e cada vídeo é
              <span className="text-white font-medium"> compartilhado todos os dias no Instagram</span>, alcançando
              milhares de atletas e seguidores. Sua marca aparece em cada lance.
            </p>

            {/* Stats principais — soma de TODAS as quadras */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8">
              <div>
                <div className="text-3xl font-extrabold" style={{ color: ORANGE_LIGHT }}>{hasData ? heroValue : 'Em breve'}</div>
                <div className="text-xs text-zinc-500 mt-0.5">vídeos {heroPeriod} · todas as quadras</div>
              </div>
              {otherNumeric > 0 && (
                <div>
                  <div className="text-3xl font-extrabold" style={{ color: ORANGE_LIGHT }}>{otherValue}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{otherPrefix}{otherPeriod}</div>
                </div>
              )}
              <div>
                <div className="text-3xl font-extrabold" style={{ color: ORANGE_LIGHT }}>{courtsCount}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{courtsCount === 1 ? 'quadra' : 'quadras'}</div>
              </div>
            </div>

            {/* Módulo de ALCANCE / Instagram (ao lado da quadra) */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-9 rounded-2xl border overflow-hidden relative"
              style={{ borderColor: 'rgba(229,118,5,0.35)', background: 'linear-gradient(135deg, rgba(229,118,5,0.12), rgba(0,0,0,0))' }}
            >
              <div className="absolute -bottom-16 -right-10 w-48 h-48 blur-[80px] opacity-20" style={{ background: ORANGE }} />
              <div className="relative p-5">
                <div className="flex items-start gap-4">
                  <RisingChart />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: ORANGE_LIGHT }}>
                      <Share2 className="w-4 h-4" /> Alcance que explode no Instagram
                    </div>
                    <p className="text-sm text-zinc-300 mt-1.5 leading-relaxed">
                      Os vídeos são postados e compartilhados <span className="text-white font-medium">todos os dias</span>,
                      com alcance de <span className="text-white font-medium">milhões de seguidores</span>. Cada lance
                      pode passar de <span className="text-white font-medium">500 a 1.000+ visualizações</span>.
                    </p>
                  </div>
                </div>
                {hasData && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-end justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-3xl md:text-4xl font-extrabold bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(120deg, #fff, ${ORANGE_LIGHT})` }}>
                        +{fmt(estViews)}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">visualizações potenciais{small ? '' : ' por mês'} com a sua marca</div>
                    </div>
                    <div className="text-[11px] text-zinc-500 text-right leading-tight">
                      cada vídeo:<br />500 a 1.000+ views
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>

          {/* Right: vídeo (portrait) */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="relative">
            <div className="absolute -inset-3 rounded-3xl blur-2xl opacity-30" style={{ background: `linear-gradient(135deg, ${ORANGE}, transparent)` }} />
            <div
              className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-black shadow-2xl mx-auto"
              style={{ aspectRatio: sample?.videoUrl ? ratio : '9 / 16', maxWidth: 440 }}
            >
              {sample?.videoUrl ? (
                <>
                  <video
                    src={sample.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (v.videoWidth && v.videoHeight) setRatio(`${v.videoWidth} / ${v.videoHeight}`);
                    }}
                    onCanPlay={() => setVideoReady(true)}
                    className="w-full h-full object-cover block"
                  />
                  <div className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0a0a0a] transition-opacity duration-500 ${videoReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    <div className="w-9 h-9 rounded-full border-2 border-zinc-700 animate-spin" style={{ borderTopColor: ORANGE }} />
                    <span className="text-xs text-zinc-500">Carregando o lance…</span>
                  </div>
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md" style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE_LIGHT }} />
                      Exemplo real · {sample.court}
                    </span>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                  <Video className="w-10 h-10 text-zinc-700" />
                  <p className="text-zinc-500 text-sm">Os primeiros lances da arena estão a caminho.</p>
                </div>
              )}
            </div>
            <p className="text-center text-xs text-zinc-600 mt-3">É assim que o vídeo chega ao atleta — com espaço para o seu anúncio.</p>
          </motion.div>
        </div>
      </section>

      {/* Stats band */}
      {hasData && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard i={0} value={`+${fmt(monthTotal)}`} label="vídeos este mês" icon={Clapperboard} />
            <StatCard i={1} value={`+de ${fmt(lastMonth)}`} label="vídeos no mês passado" icon={TrendingUp} />
            <StatCard i={2} value={courtsCount} label={courtsCount === 1 ? 'quadra ativa' : 'quadras ativas'} icon={Trophy} />
            <StatCard i={3} value={<span className="flex items-center gap-1">∞</span>} label="alcance ao compartilhar" icon={Share2} />
          </div>

          {perCourt.length > 1 && (
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="mt-5 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
              <p className="text-sm text-zinc-400 mb-4">Vídeos por quadra este mês <span className="text-zinc-600">· somando todas: +{fmt(monthTotal)}</span></p>
              <div className="space-y-3">
                {perCourt.map((c: any) => {
                  const pct = monthTotal ? Math.round((c.monthCount / monthTotal) * 100) : 0;
                  return (
                    <div key={c.name} className="flex items-center gap-4">
                      <div className="w-32 shrink-0 text-sm text-zinc-300 truncate">{c.name}</div>
                      <div className="flex-1 h-2.5 rounded-full bg-zinc-800 overflow-hidden">
                        <motion.div initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }} transition={{ duration: 0.9, ease: 'easeOut' }} className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${ORANGE}, ${ORANGE_LIGHT})` }} />
                      </div>
                      <div className="w-12 text-right text-sm font-semibold text-white">{c.monthCount}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </section>
      )}

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl mb-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Como funciona o patrocínio</h2>
          <p className="text-zinc-400 mt-3">Simples, flexível e mensurável. Sua marca entra no conteúdo que o atleta já quer ver.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          <Pillar i={0} icon={Megaphone} title="Seu logo onde você quiser" text="Posicionamos a sua marca nos vídeos da arena — canto, placar, abertura ou marca d'água. Você escolhe onde e como aparecer." />
          <Pillar i={1} icon={Share2} title="Divulgação que se multiplica" text="Os atletas baixam e postam seus lances no Instagram e WhatsApp. Cada compartilhamento leva a sua marca a novos públicos — de graça, organicamente." />
          <Pillar i={2} icon={Eye} title="Exposição toda semana" text="Não é um banner parado. São dezenas de vídeos novos por mês, com a sua marca rodando em todas as partidas, repetidamente." />
          <Pillar i={3} icon={Target} title="Público de alta renda" text="Beach tennis e areia reúnem um público qualificado e engajado — o mesmo perfil que grandes marcas como Corona e Adidas já buscam no esporte." />
          <Pillar i={4} icon={Users} title="Marca da comunidade" text="Patrocinar a quadra local conecta sua empresa à comunidade. Quem joga aqui lembra de quem apoia o esporte da cidade." />
          <Pillar i={5} icon={BadgeDollarSign} title="Investimento que se paga" text="Por uma fração do custo de uma mídia tradicional, sua marca aparece em centenas de vídeos vistos, salvos e compartilhados mês após mês." />
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative rounded-3xl border border-zinc-800 overflow-hidden p-10 md:p-14 text-center" style={{ background: 'linear-gradient(135deg, rgba(229,118,5,0.10), rgba(0,0,0,0))' }}>
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[500px] h-[300px] blur-[120px] opacity-20" style={{ background: ORANGE }} />
          <Sparkles className="w-8 h-8 mx-auto mb-5" style={{ color: ORANGE_LIGHT }} />
          <h2 className="relative text-3xl md:text-4xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
            Sua marca, em campo, em cada lance da {arenaName}.
          </h2>
          <p className="relative text-zinc-400 mt-4 max-w-xl mx-auto">
            {hasData
              ? `São ${heroValue.toLowerCase()} vídeos ${heroPeriod} esperando pela sua marca — vistos e compartilhados todos os dias. Fale com a arena e garanta sua cota.`
              : 'As câmeras já estão prontas. Garanta sua cota antes dos primeiros vídeos rodarem.'}
          </p>
        </motion.div>
        <p className="text-center text-xs text-zinc-600 mt-10">
          Powered by <span style={{ color: ORANGE_LIGHT }}>Apertai</span> — câmeras inteligentes para arenas esportivas.
        </p>
      </section>
    </div>
  );
}

export default Sponsor;
